import React from 'react';
import clsx from 'clsx';

import { colorSet } from '@components/Provider';

import {
  CircularProgress,
  CircularProgressProps,
  IconButton,
  makeStyles
} from '@material-ui/core';

import { Close } from '@material-ui/icons';
import { Download } from '@carbon/icons-react';

interface Props
  extends Omit<
    CircularProgressProps,
    'value' | 'classes' | 'color' | 'variant'
  > {
  value?: number;
  variant?: 'download' | 'cancel';
  classes?: {
    root?: string;
    track?: string;
    progress?: string;
  };
  color?: 'gray' | 'primary';
  onDownload?: () => void;
  onCancel?: () => void;
}

const CustomizedCircularProgress = ({
  classes: outerClasses,
  value = 0,
  color = 'gray',
  size = 32,
  thickness = 3,
  variant = 'download',
  onCancel,
  onDownload
}: Props) => {
  const innerClasses = useStyles();

  return (
    <div
      className={clsx(
        innerClasses['Customized-Progress-root'],
        outerClasses?.root
      )}
    >
      {/* background gray track */}
      <CircularProgress
        className={clsx([
          innerClasses['Customized-Progress-Circle-Track'],
          {
            gray: color === 'gray',
            primary: color === 'primary'
          },
          outerClasses?.track
        ])}
        variant="determinate"
        value={100}
        size={size}
        thickness={0}
      />

      <IconButton
        className={clsx(innerClasses['Customized-Progress-Close-button'], {
          gray: color === 'gray',
          primary: color === 'primary'
        })}
        onClick={(e) => {
          e.stopPropagation();
          if (variant === 'download' && onDownload) {
            onDownload();
          }

          if (variant === 'cancel' && onCancel) {
            onCancel();
          }
        }}
      >
        {variant === 'download' ? <Download /> : <Close />}
      </IconButton>

      {/* primary circle for real progress ratio */}
      <CircularProgress
        className={clsx(
          innerClasses['Customized-Progress-Circle'],
          {
            gray: color === 'gray',
            primary: color === 'primary'
          },
          outerClasses?.progress
        )}
        variant="determinate"
        color="primary"
        value={value}
        size={size}
        thickness={thickness}
      />
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  'Customized-Progress-root': {
    width: 32,
    height: 32,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  'Customized-Progress-Circle': {
    position: 'absolute',
    top: 0,
    left: 0,
    color: colorSet.gray000,
    '&.gray': {
      color: colorSet.gray000
    },
    '&.primary': {
      color: colorSet.primary500
    }
  },
  'Customized-Progress-Circle-Track': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 32,
    height: 32,
    borderRadius: '50%',
    '&.gray': {
      backgroundColor: 'rgba(52, 58, 64, 0.7)'
    },
    '&.primary': {
      width: 32,
      height: 32,
      backgroundColor: colorSet.gray000,
      border: `1px solid ${colorSet.gray200}`
    }
  },
  'Customized-Progress-Close-button': {
    padding: 0,
    width: 16,
    height: 16,
    zIndex: 1,
    '& svg': {
      width: 16,
      height: 16
    },
    '&.gray': {
      color: colorSet.gray000
    },
    '&.primary': {
      color: colorSet.gray1000
    }
  }
}));

export default CustomizedCircularProgress;
